import { useEffect, useState } from "react";
import { useData } from "../context/DataContext";
import {
  projectMonthlyRaw, projectMonthlyWeighted,
  projectRawTotal, projectWeightedTotal, months,
} from "../lib/calc";
import { fmtMoney, fmtPct, fmtMonthShort, contractLabel } from "../lib/format";
import { toast } from "sonner";
import InfoTip from "./InfoTip";
import { X } from "lucide-react";

export default function ProjectDrawer({ project, onClose }) {
  const { data, updateNote } = useData();
  const [note, setNote] = useState(project?.note || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setNote(project?.note || ""); }, [project]);

  if (!project) return null;
  const m = months(data);
  const principal = data.principals.find((p) => p.id === project.principalId);
  const peopleMap = Object.fromEntries(data.people.map((p) => [p.id, p]));
  const monthlyRaw = projectMonthlyRaw(project, m);
  const monthlyWeighted = projectMonthlyWeighted(project, m);
  const raw = projectRawTotal(project);
  const wtd = projectWeightedTotal(project);

  const saveNote = async () => {
    setSaving(true);
    try {
      await updateNote(project.id, note);
      toast.success("Note saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the note — check your connection and try again");
    } finally {
      setSaving(false);
    }
  };

  const chipClass = project.contract === "Yes" ? "chip-yes"
    : project.contract === "Tentative" ? "chip-tentative" : "chip-no";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 animate-fade" onClick={onClose} data-testid="project-drawer-backdrop">
      <aside
        className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white border-l border-[var(--rule-strong)] overflow-auto thin-scroll animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="project-drawer"
      >
        <div className="p-8 border-b border-[var(--rule)]">
          <div className="flex justify-between items-start">
            <div>
              <div className="eyebrow">{project.id} · {principal?.name || "—"}</div>
              <h3 className="font-display text-2xl mt-2" data-testid="drawer-project-name">{project.name}</h3>
              <span className={`chip ${chipClass} mt-3`} data-testid="drawer-contract">{contractLabel(project.contract)}</span>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--paper-soft)]" data-testid="drawer-close">
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-px bg-[var(--rule)] mt-6 border border-[var(--rule)]">
            <div className="bg-white p-4">
              <div className="eyebrow inline-flex items-center">
                Raw total
                <InfoTip side="bottom" label="Raw total">
                  Sum of every scope's monthly fees at face value — no probability applied.
                </InfoTip>
              </div>
              <div className="font-mono-num text-2xl mt-1" data-testid="drawer-raw-total">{fmtMoney(raw)}</div>
            </div>
            <div className="bg-white p-4">
              <div className="eyebrow inline-flex items-center">
                Weighted
                <InfoTip side="bottom" label="Weighted total">
                  Each scope's fees multiplied by its probability, then summed. A more cautious view of likely revenue.
                </InfoTip>
              </div>
              <div className="font-mono-num text-2xl mt-1" data-testid="drawer-weighted-total">{fmtMoney(wtd)}</div>
            </div>
          </div>
        </div>

        {/* Monthly by stage */}
        <div className="p-8 border-b border-[var(--rule)]">
          <div className="section-h mb-4">Monthly revenue by stage</div>
          <div className="overflow-auto thin-scroll">
            <table className="w-full text-xs font-mono-num">
              <thead>
                <tr className="text-[var(--soft-fg)] border-b border-[var(--rule-strong)]">
                  <th className="text-left py-2 pr-4 font-medium">Stage · Prob</th>
                  {m.map((mo) => (
                    <th key={mo} className="text-right py-2 px-2 font-medium">{fmtMonthShort(mo)}</th>
                  ))}
                  <th className="text-right py-2 pl-3 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {project.scopes.map((s) => {
                  const total = Object.values(s.revenue || {}).reduce((a,b)=>a+(b||0),0);
                  return (
                    <tr key={s.id} className="border-b border-[var(--rule)]">
                      <td className="py-2 pr-4">
                        <div className="text-[var(--ink)]">{s.stage}</div>
                        <div className="text-[10px] text-[var(--soft-fg)]">P · {fmtPct(s.probability ?? 1)}</div>
                      </td>
                      {m.map((mo) => (
                        <td key={mo} className="text-right py-2 px-2 text-[var(--ink)]">
                          {s.revenue?.[mo] ? fmtMoney(s.revenue[mo], { compact: true }) : "—"}
                        </td>
                      ))}
                      <td className="text-right py-2 pl-3 text-[var(--ink)]">{fmtMoney(total)}</td>
                    </tr>
                  );
                })}
                <tr className="text-[var(--ink)]">
                  <td className="py-2 pr-4 eyebrow">Total raw</td>
                  {m.map((mo) => (
                    <td key={mo} className="text-right py-2 px-2">
                      {monthlyRaw[mo] ? fmtMoney(monthlyRaw[mo], { compact: true }) : "—"}
                    </td>
                  ))}
                  <td className="text-right py-2 pl-3">{fmtMoney(raw)}</td>
                </tr>
                <tr className="text-[var(--soft-fg)]">
                  <td className="py-2 pr-4 eyebrow">Weighted</td>
                  {m.map((mo) => (
                    <td key={mo} className="text-right py-2 px-2">
                      {monthlyWeighted[mo] ? fmtMoney(monthlyWeighted[mo], { compact: true }) : "—"}
                    </td>
                  ))}
                  <td className="text-right py-2 pl-3">{fmtMoney(wtd)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Allocations */}
        <div className="p-8 border-b border-[var(--rule)]">
          <div className="section-h mb-4">People allocated</div>
          <table className="w-full text-xs font-mono-num">
            <thead>
              <tr className="text-[var(--soft-fg)] border-b border-[var(--rule-strong)]">
                <th className="text-left py-2 pr-4 font-medium">Person</th>
                {m.map((mo) => (
                  <th key={mo} className="text-right py-2 px-2 font-medium">{mo.slice(0,3)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {project.allocations.map((a) => (
                <tr key={a.personId} className="border-b border-[var(--rule)]">
                  <td className="py-2 pr-4 text-[var(--ink)]">{peopleMap[a.personId]?.name || a.personId}</td>
                  {m.map((mo) => (
                    <td key={mo} className="text-right py-2 px-2 text-[var(--ink)]">
                      {a.load?.[mo] ? `${Math.round(a.load[mo]*100)}%` : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Notes */}
        <div className="p-8 pb-24">
          <div className="section-h mb-4">Notes</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note for this project…"
            className="w-full h-32 p-3 border border-[var(--rule)] font-mono-num text-xs outline-none focus:border-[var(--ink)] bg-[var(--paper-soft)]"
            data-testid="drawer-note-input"
          />
          <div className="flex justify-start mt-3">
            <button
              onClick={saveNote}
              disabled={saving}
              className="px-4 py-2 text-xs font-display tracked-tight uppercase bg-[var(--ink)] text-white disabled:opacity-40"
              data-testid="drawer-save-note"
            >
              {saving ? "Saving…" : "Save note"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
